import { useState, useEffect } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

const TeamCarousel = ({ teamMembers }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [visibleCount, setVisibleCount] = useState(3)

  // Determine how many team members to show based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleCount(1)
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2)
      } else {
        setVisibleCount(3)
      }
    }

    handleResize() // Set initial value
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const totalSlides = Math.ceil(teamMembers.length / visibleCount)

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex === totalSlides - 1 ? 0 : prevIndex + 1))
  }

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? totalSlides - 1 : prevIndex - 1))
  }

  const goToSlide = (index) => {
    setCurrentIndex(index)
  }

  // Auto-advance the carousel
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide()
    }, 5000)

    return () => clearInterval(interval)
  }, [currentIndex, totalSlides])

  // Calculate which team members to show
  const startIndex = currentIndex * visibleCount
  const visibleTeamMembers = teamMembers.slice(startIndex, startIndex + visibleCount)

  return (
    <div className="team-carousel relative">
      {/* Carousel content */}
      <div className="overflow-hidden">
        <div className="flex transition-all duration-500 ease-in-out">
          {visibleTeamMembers.map((member, index) => (
            <div
              key={startIndex + index}
              className="w-full sm:w-1/2 lg:w-1/3 px-4 flex-shrink-0"
              style={{
                flex: `0 0 ${100 / visibleCount}%`,
              }}
            >
              <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
                <div className="relative pb-[75%] overflow-hidden">
                  <img
                    src={member.image || "/placeholder.svg"}
                    alt={member.name}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                  <p className="text-purple-600 font-medium mb-2">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                  <div className="mt-4 flex space-x-3">
                    {member.socialLinks.map((social, idx) => (
                      <a
                        key={idx}
                        href={social.url}
                        className="text-gray-500 hover:text-purple-600 transition-colors"
                        aria-label={social.platform}
                      >
                        {social.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation buttons */}
      <button
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-white rounded-full p-2 shadow-md z-10 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 sm:-translate-x-0"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6 text-purple-600" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 bg-white rounded-full p-2 shadow-md z-10 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 sm:translate-x-0"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6 text-purple-600" />
      </button>

      {/* Indicators */}
      <div className="flex justify-center mt-6 space-x-2">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors ${
              index === currentIndex ? "bg-purple-600" : "bg-gray-300"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

export default TeamCarousel

